import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import { Calendar2, CheckmarkCircleOutlineLegacy } from '@lyearn/icons';
import { tagsFilterConfig } from '@/components/FilterManager/configs/tags/tagsFilterConfig';
import { SkillEntityConfig } from '@/components/FilterManager/EntityFilter/entity/skills';
import { UserEntityConfig } from '@/components/FilterManager/EntityFilter/entity/user';
import { FilterManager, SortAndFilterItem } from '@/components/FilterManager/types';
import { FeatureFlagsMap } from '@/helper/FeatureFlags';
import { AppConfig } from '@/hooks/useAppConfig';
import {
  AssignmentTypeEnum,
  EventSelectionInput,
  EventStatusEnum,
  RecurringTypeEnum,
  TagAvailableOnEnum,
  UserTypeEnum,
} from '@/types/schema';

import { SessionTabs } from '../../../events/views/EventManager/types';
import {
  getEventSortItems,
  getEventTypeFilterItem,
} from '../../../events/views/EventManager/utils';
import { getContentTypeOptions } from '../../modules/admin/views/LearningContent/helpers/getContentType';
import { CONTENT_SORT_ITEMS } from '../../modules/admin/views/LearningContent/helpers/getSortAndFilterItems';
import { ALL_CONTENT_TYPES } from '../../modules/assignment/views/AssignmentCreator/components/ContentAndCollectionList/utils';
import { Tabs } from './types';

export const OVERRIDES = {
  Tabs: {
    classes: { flexContainer: 'space-s-24' },
  },
  Tab: {
    classes: { root: 'min-w-0 p-0 opacity-100 heading-xs py-8 normal-case' },
  },
};

export const getTabs = (t: TFunction, dialogConfig: Tabs[]) =>
  getEnabledItems([
    {
      label: t('Admin:GlobalContent.Content'),
      value: Tabs.Content,
      enabled: dialogConfig.includes(Tabs.Content),
    },
    {
      label: t('Common:Sessions'),
      value: Tabs.Sessions,
      enabled: dialogConfig.includes(Tabs.Sessions),
    },
    {
      label: t('Admin:GlobalContent.Collections'),
      value: Tabs.Collections,
      enabled: dialogConfig.includes(Tabs.Collections),
    },
  ]);

export const TRANSLATIONS = {
  [Tabs.Collections]: {
    noContentsFound: {
      title: 'Admin:GlobalContent.CollectionNotFound',
      description: 'Admin:GlobalContent.NoResultsFound',
    },
    noContentsAvailable: {
      title: 'Admin:GlobalContent.NoCollection',
      description: 'Admin:GlobalContent.NoCollectionDescription',
    },
  },
  [Tabs.Content]: {
    noContentsFound: {
      title: 'Admin:GlobalContent.LearningContentNotFound',
      description: 'Admin:GlobalContent.NoResultsFound',
    },
    noContentsAvailable: {
      title: 'Admin:GlobalContent.NoLearningContent',
      description: 'Admin:GlobalContent.NoLearningContentDescription',
    },
  },
  [Tabs.Sessions]: {
    noContentsFound: {
      title: 'Admin:GlobalContent.SessionNotFound',
      description: 'Admin:GlobalContent.NoResultsFound',
    },
    noContentsAvailable: {
      title: 'Admin:GlobalContent.SessionNotFound',
      description: 'Admin:GlobalContent.NoLearningSessionDescription',
    },
  },
};

export const DEFAULT_DIALOG_ACTIONS = {
  isAlreadySelected: false,
  isMultiAllowed: true,
  loading: false,
  isAssingmentTypeChangeAllowed: false,
  showDuration: true,
};

export const DEFAULT_DIALOG_CONFIG = {
  eventStatus: [],
  contentTypes: ALL_CONTENT_TYPES,
  tabConfig: [Tabs.Content, Tabs.Collections],
  recurringCategories: [RecurringTypeEnum.NonRecurring, RecurringTypeEnum.RecurringInstance],
};

export const getDefaultStrings = (t: TFunction) => {
  return {
    title: t('Admin:GlobalContent.Title'),
    description: t('Admin:GlobalContent.Description'),
    primaryButtonText: t('Admin:GlobalContent.AddContent'),
    secondaryButtonText: t('Common:Cancel'),
  };
};

export const getContentSortAndFilterItems = (
  t: TFunction,
  appConfig: AppConfig,
  featureFlags: FeatureFlagsMap,
): SortAndFilterItem[] => {
  return [
    {
      type: FilterManager.Sort,
      isEnabled: true,
      items: CONTENT_SORT_ITEMS,
    },
    {
      type: FilterManager.Filter,
      id: 'types',
      defaultPlaceholder: t('Common:Filter.TypeOfContent'),
      isEnabled: true,
      groups: [
        {
          id: 'resourceType',
          options: getContentTypeOptions(t, appConfig, featureFlags),
        },
      ],
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'createdByIds',
      defaultPlaceholder: t('Common:Filter.CreatedBy'),
      entity: 'user',
      listConfig: UserEntityConfig,
      queryParams: {
        userTypes: [UserTypeEnum.Staff],
      },
    },
    {
      type: FilterManager.DateRange,
      id: 'createdAt',
      defaultPlaceholder: t('Common:Filter.CreatedOn'),
      isEnabled: true,
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'skillIds',
      defaultPlaceholder: t('Common:Filter.Skills'),
      entity: 'skill',
      listConfig: SkillEntityConfig,
    },
    {
      ...tagsFilterConfig(t),
      queryParams: {
        availableOn: [TagAvailableOnEnum.Content],
      },
    },
  ];
};

export const getSessionSortItems = (t: TFunction) => getEventSortItems(t, SessionTabs.Finished);

export const getSessionSortAndFilterItems = (
  t: TFunction,
  appConfig: AppConfig,
): SortAndFilterItem[] => {
  return [
    {
      type: FilterManager.Sort,
      isEnabled: true,
      items: getSessionSortItems(t),
    },
    getEventTypeFilterItem({ t }, appConfig),
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'instructorIds',
      defaultPlaceholder: t('Common:Filter.Instructor'),
      entity: 'user',
      listConfig: UserEntityConfig,
      queryParams: {
        userTypes: [UserTypeEnum.Learner, UserTypeEnum.Staff],
      },
    },
    {
      type: FilterManager.Filter,
      id: 'status',
      defaultPlaceholder: t('Common:Sort.Status'),
      isEnabled: true,
      groups: [
        {
          id: 'eventStatus',
          options: getEnabledItems([
            {
              Icon: Calendar2,
              value: EventStatusEnum.Scheduled,
              label: t('Common:Sort.Upcoming'),
              enabled: true,
            },
            {
              Icon: CheckmarkCircleOutlineLegacy,
              value: EventStatusEnum.Completed,
              label: t('Common:Filter.Finished'),
              enabled: true,
            },
          ]),
        },
      ],
    },
    {
      type: FilterManager.Entity,
      isEnabled: false,
      id: 'skillIds',
      defaultPlaceholder: t('Common:Filter.Skills'),
      entity: 'skill',
      listConfig: SkillEntityConfig,
    },
    {
      ...tagsFilterConfig(t),
      queryParams: {
        availableOn: [TagAvailableOnEnum.Event],
      },
    },
  ];
};

export const getEventSelectionInput = (ids?: string[]): EventSelectionInput[] => {
  if (!ids) {
    return [];
  }

  return ids.map((id) => ({ eventId: id, assignmentType: AssignmentTypeEnum.All }));
};

export const getEventIdsFromSelectionInput = (eventSelections?: EventSelectionInput[]) => {
  if (!eventSelections?.length) {
    return [];
  }

  return eventSelections.map(({ eventId }) => eventId);
};
